import type { StaffProfileDetails, StaffRating } from '../../types/staff';
import { STAFF_SIGNAL_KEYS } from '../../types/staff';
import { StaffRatingBadge } from './StaffRatingBadge';
import { EvidenceChip } from './EvidenceChip';
import { cn } from '../../utils/cn';
import { isStrongRating, stripEvidenceRefs } from '../../utils/staffProfile';
import { Star } from 'lucide-react';

interface StaffSignalCardsProps {
  profile: StaffProfileDetails;
}

const METER: Record<string, { filled: number; color: string }> = {
  strong: { filled: 3, color: 'bg-purple-400' },
  solid: { filled: 3, color: 'bg-green-400' },
  good: { filled: 3, color: 'bg-green-400' },
  mixed: { filled: 2, color: 'bg-yellow-400' },
  weak: { filled: 1, color: 'bg-red-400' },
  poor: { filled: 1, color: 'bg-red-400' },
};

/**
 * Three-stage meter (weak / mixed / solid). The rare `strong` rating fills all three
 * stages in purple and adds a star cap so it reads as "beyond solid" at a glance.
 */
const Meter = ({ rating }: { rating: StaffRating | string }) => {
  const spec = METER[rating] ?? { filled: 0, color: 'bg-glass-400' };
  const strong = isStrongRating(rating);
  return (
    <div className="flex items-center gap-1" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <span key={i} className={cn('h-1.5 flex-1 rounded-full', i < spec.filled ? spec.color : 'bg-glass-200/15')} />
      ))}
      {strong && <Star className="w-3.5 h-3.5 text-purple-300 fill-current shrink-0 -my-1" />}
    </div>
  );
};

/**
 * The five behavioural signals as meter cards. The grid fits the container width
 * (auto-fit), so cards stay readable whether or not the sidebar is pinned.
 */
export const StaffSignalCards = ({ profile }: StaffSignalCardsProps) => (
  <div className="grid grid-cols-[repeat(auto-fit,minmax(230px,1fr))] gap-3">
    {STAFF_SIGNAL_KEYS.map(({ key, label }) => {
      const signal = profile[key];
      if (!signal) return null;
      const strong = isStrongRating(signal.rating);
      return (
        <article
          key={key}
          className={cn(
            'glass-card relative overflow-hidden p-4 flex flex-col gap-3 transition-colors',
            strong
              ? 'border-purple-400/40 bg-purple-500/[0.05] ring-1 ring-purple-400/20 hover:border-purple-400/60'
              : 'hover:border-accent-400/30',
          )}
        >
          <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1.5">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-glass-300 leading-5 whitespace-nowrap">{label}</h3>
            <StaffRatingBadge rating={signal.rating} />
          </div>
          <Meter rating={signal.rating} />
          {/* Citations are stripped from the note: the evidence chips below already list them. */}
          <p className="text-[13px] text-glass-300 leading-relaxed flex-1">{stripEvidenceRefs(signal.note)}</p>
          <div className="flex flex-wrap items-center gap-1 pt-2 border-t border-glass-200/10">
            <span className="text-[11px] font-mono text-glass-500 mr-1">n={signal.sample_size}</span>
            {signal.evidence_ids.map((id) => (
              <EvidenceChip key={id} id={id} />
            ))}
          </div>
        </article>
      );
    })}
  </div>
);
